import { useState, useEffect } from "react";
import { Printer, FileText, ChevronDown, ChevronUp, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { printDepositTicket } from "@/utils/printTicket";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DepositHistory({ customerId, getAuth }) {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [viewId, setViewId] = useState(null);

  useEffect(() => {
    const headers = getAuth();
    if (!headers) return;
    axios.get(API + "/api/admin/customers/" + customerId + "/deposit-slips", { headers })
      .then(res => setDeposits(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [customerId]);

  const handleReprint = (deposit) => {
    printDepositTicket(deposit);
  };

  const toggleView = (id) => {
    setViewId(viewId === id ? null : id);
  };

  if (loading || deposits.length === 0) return null;

  return (
    <div className="mt-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-orange-600" />
          <span className="font-medium text-orange-800 text-sm">{deposits.length} bon(s) de dépôt</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-orange-600" /> : <ChevronDown className="w-4 h-4 text-orange-600" />}
      </button>
      {expanded && (
        <div className="mt-3 space-y-2">
          {deposits.slice(0, 5).map((d) => (
            <DepositCard key={d.id} deposit={d} isOpen={viewId === d.id} onToggle={() => toggleView(d.id)} onPrint={() => handleReprint(d)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DepositCard({ deposit, isOpen, onToggle, onPrint }) {
  const count = deposit.articles ? deposit.articles.length : 0;
  const dateStr = new Date(deposit.created_at).toLocaleDateString("fr-FR");
  
  return (
    <div className="bg-white p-2 rounded border border-orange-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-orange-500 text-white">{deposit.ticket_number || "Sans n°"}</Badge>
          <span className="text-slate-600">{dateStr} - {count} article(s)</span>
        </div>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onToggle} className="text-blue-600 hover:bg-blue-100 h-7 px-2">
            <Eye className="w-4 h-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onPrint} className="text-orange-600 hover:bg-orange-100 h-7 px-2">
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {isOpen && <DepositDetails deposit={deposit} />}
    </div>
  );
}

function DepositDetails({ deposit }) {
  const articles = deposit.articles || [];
  return (
    <div className="mt-2 pt-2 border-t border-orange-100 text-xs space-y-2">
      <p><span className="text-slate-500">Client:</span> <span className="font-medium">{deposit.customer_name}</span></p>
      <p><span className="text-slate-500">Total:</span> <span className="font-medium">{deposit.total_amount}€</span></p>
      <div>
        <span className="text-slate-500">Articles:</span>
        <ul className="mt-1 space-y-1">
          {articles.map((a, i) => (
            <li key={i} className="flex justify-between bg-slate-50 px-2 py-1 rounded">
              <span>{a.quantity}x {a.name}</span>
              <span>{a.unit_price}€</span>
            </li>
          ))}
        </ul>
      </div>
      {deposit.remarks && <p className="bg-yellow-50 p-2 rounded"><span className="text-slate-500">Remarques:</span> {deposit.remarks}</p>}
    </div>
  );
}
